<template>
  <CardExpand
    elevation="0"
    somente-icone
    class="pa-0"
    :expandido="expandido"
    @change:expandido="expandido = $event"
  >
    <template #title>
      <div class="d-flex flex-row align-center" @click.stop>
        <v-simple-checkbox
          :value="isSelecionado"
          @input="$emit('toggle-check', $event)"
          hide-details
          class="ma-0 pa-0"
          color="primary"
        />
        <label class="body-2 font-weight-bold" style="color: #088cca">
          {{ title }}
        </label>
      </div>
    </template>

    <div
      ref="textoComTags"
      class="texto-com-tags"
      v-html="highlightedDesc"
    ></div>
    <span v-if="jurisprudencia?.deEmentaJuris">
      <strong class="mr-1">Ementa:</strong
      >{{ jurisprudencia?.deEmentaJuris ?? '' }}
    </span>

    <v-row
      class="ma-0 mt-4 align-center"
      dense
      style="cursor: pointer"
      @click="showDetalhes = !showDetalhes"
    >
      <v-btn icon>
        <v-icon size="24">{{
          showDetalhes ? mdiChevronUp : mdiChevronDown
        }}</v-icon>
      </v-btn>
      <label>Detalhes</label>
    </v-row>

    <v-divider :class="{ 'mb-4': !showDetalhes }" />

    <v-expand-transition>
      <div v-show="showDetalhes" v-if="jurisprudencia?.nmComarca">
        <v-row dense class="ma-0 mt-4">
          <sds-text-view label="Comarca:" label-bold class="mr-1" />
          <span>{{ jurisprudencia?.nmComarca ?? '' }}</span>
        </v-row>
        <v-row dense class="ma-0" v-if="jurisprudencia?.nmOrgaoJulgador">
          <sds-text-view label="Órgão julgador:" label-bold class="mr-1" />
          <span>{{ jurisprudencia?.nmOrgaoJulgador ?? '' }}</span>
        </v-row>
        <v-row dense class="ma-0" v-if="jurisprudencia?.deRelator">
          <sds-text-view label="Relator:" label-bold class="mr-1" />
          <span>{{ jurisprudencia?.deRelator ?? '' }}</span>
        </v-row>
        <v-row dense class="ma-0" v-if="jurisprudencia?.dtJulgamento">
          <sds-text-view label="Data de julgamento:" label-bold class="mr-1" />
          <span>{{ jurisprudencia?.dtJulgamento ?? '' }}</span>
        </v-row>
        <v-row dense class="ma-0" v-if="jurisprudencia?.deVotacao">
          <sds-text-view label="Votação:" label-bold class="mr-1" />
          <span>{{ jurisprudencia?.deVotacao ?? '' }}</span>
        </v-row>
        <v-row dense class="ma-0" v-if="jurisprudencia?.deVoto">
          <sds-text-view label="Voto:" label-bold class="mr-1" />
          <span>{{ jurisprudencia?.deVoto ?? '' }}</span>
        </v-row>
        <v-row
          dense
          class="ma-0"
          v-if="
            jurisprudencia?.boletimList && jurisprudencia.boletimList.length
          "
        >
          <sds-text-view label="Boletins:" label-bold class="mr-1" />
          <ul class="ma-0">
            <li v-for="boletim in jurisprudencia.boletimList" :key="boletim">
              <a
                :href="`/mat-report/publico/gcn/api/v1.0/boletim?boletins=${boletim.cdBoletim}`"
                target="_blank"
              >
                {{
                  boletim.deTitulo + ', ' + boletim.nuMes + '/' + boletim.nuAno
                }}
              </a>
            </li>
          </ul>
        </v-row>
        <v-row dense class="ma-0" v-if="prolatores">
          <sds-text-view label="Magistrado Prolator:" label-bold class="mr-1" />
          <span>{{ prolatores }}</span>
        </v-row>
      </div>
    </v-expand-transition>

    <v-row dense class="ma-0 d-flex justify-end">
      <SeletorOpcoesImpressão
        class="pa-0"
        color="primary"
        :items="itensImpressao"
        @change="download"
      />
    </v-row>
  </CardExpand>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import CardExpand from '@/views/components/CardExpand.vue';
import SeletorOpcoesImpressão from '@/views/components/SeletorOpcoesImpressão.vue';
import { IJurisprudencia } from '../../types';
import IconPaginaSimplificada from '@/views/components/icons/IconPaginaSimplificada.vue';
import IconPaginaDetalhada from '@/views/components/icons/IconPaginaDetalhada.vue';
import IconTextoNorma from '@/views/components/icons/IconTextoNorma.vue';
import JurisprudenciaSelecionadaService from '@/views/jurisprudencia-selecionada/service';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import i18n from '@/plugins/vue-i18n';
import SdsSnackbarQueue from '@ungp/sds-snackbar-queue';

interface IProps {
  jurisprudencia?: IJurisprudencia;
  isSelecionado: boolean;
}

const props = defineProps<IProps>();

defineEmits(['toggle-check']);

const expandido = ref(true);

const showDetalhes = ref(false);

const itensImpressao = [
  {
    text: 'Acórdãos',
    value: 'A',
    icone: IconTextoNorma,
  },
  {
    text: 'Simplificado',
    value: 'S',
    icone: IconPaginaSimplificada,
  },
  {
    text: 'Detalhado',
    value: 'D',
    icone: IconPaginaDetalhada,
  },
];

const title = computed(
  () =>
    `${props.jurisprudencia?.deClasse || ''} ${
      props.jurisprudencia?.nuProcOrigem || ''
    }`
);

const highlightedDesc = computed(
  () => props.jurisprudencia?.highlightedDesc || ''
);

const prolatores = computed(() => {
  if (
    !props.jurisprudencia?.prolatores ||
    !props.jurisprudencia.prolatores.length
  ) {
    return '';
  }

  if (props.jurisprudencia.prolatores.length > 1) {
    return props.jurisprudencia.prolatores
      .map((prolator) => prolator.nmProlator)
      .join(', ');
  }

  return props.jurisprudencia.prolatores[0].nmProlator;
});

watch(
  () => props.jurisprudencia,
  async (newValue, oldValue) => {
    if (newValue != oldValue) {
      expandido.value = true;
      showDetalhes.value = false;
    }
  },
  { deep: true }
);

const download = (tipoImpressao: 'A' | 'S' | 'D') => {
  const cdAcordao = props.jurisprudencia?.cdAcordao;
  const cdJurisprudencia = props.jurisprudencia?.cdJurisprudencia;
  const flSegredoJustica = props.jurisprudencia?.flSegredoJustica;

  if (tipoImpressao === 'A' && flSegredoJustica === 'S' && cdAcordao) {
    SdsSnackbarQueue.error(i18n.t('label.AVISO_JURISPRUDENCIA_SEGREDO_JUSTICA') as string);
    return;
  }

  if (tipoImpressao === 'A' && flSegredoJustica === 'N' && cdAcordao) {
    JurisprudenciaSelecionadaService.downloadAcordao(cdAcordao);
  }

  if (tipoImpressao === 'S' && cdJurisprudencia) {
    JurisprudenciaSelecionadaService.downloadSimplificado([cdJurisprudencia]);
  }

  if (tipoImpressao === 'D' && cdJurisprudencia) {
    JurisprudenciaSelecionadaService.downloadDetalhado([cdJurisprudencia]);
  }
};
</script>

<style lang="scss" scoped>
.texto-com-tags {
  line-height: normal;
  ::v-deep span {
    background-color: #e1f6fc !important;
    padding: 0 4px;
    font-weight: bold;
  }
}

ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
}
</style>
